import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Activity, Database, Cloud, AlertCircle, Clock, Cpu, HardDrive, TrendingUp, Settings, RefreshCw } from 'lucide-react';
import { getSystemHealth, getSystemMetrics } from '../api/dashboard';
import { Card, CardContent } from '../components/ui/Card';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import GaugeChart from '../components/charts/GaugeChart';
import TrendChart from '../components/charts/TrendChart';
import toast from 'react-hot-toast';

const PerformancePageEnhanced = () => {
  const [health, setHealth] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showThresholds, setShowThresholds] = useState(false);
  const [thresholds, setThresholds] = useState({
    cpuWarning: 70,
    cpuCritical: 90,
    memoryWarning: 75,
    memoryCritical: 90,
    errorRateWarning: 5,
    errorRateCritical: 10
  });

  useEffect(() => {
    loadPerformanceData();
  }, []);

  const loadPerformanceData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const [healthData, metricsData] = await Promise.all([
        getSystemHealth(),
        getSystemMetrics()
      ]);
      
      setHealth(healthData.data);
      setMetrics(metricsData.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to load performance data:', error);
      if (error.response?.status !== 401) {
        toast.error('Failed to load performance data');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  const handleRefresh = () => {
    loadPerformanceData(true);
  };

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const getStatusColor = (status) => {
    return status === 'healthy' ? 'text-green-600' : 'text-red-600';
  };

  const getStatusBg = (status) => {
    return status === 'healthy' ? 'bg-green-100' : 'bg-red-100';
  };

  const getThresholdStatus = (value, warning, critical) => {
    if (value >= critical) return { color: 'text-red-600', bg: 'bg-red-100', label: 'Critical' };
    if (value >= warning) return { color: 'text-yellow-600', bg: 'bg-yellow-100', label: 'Warning' };
    return { color: 'text-green-600', bg: 'bg-green-100', label: 'Normal' };
  };

  if (loading && !health) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <motion.div 
        className="mb-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">System Performance</h1>
            <div className="flex items-center space-x-3">
              <p className="text-gray-600">Real-time health, connectivity, and operational metrics.</p>
              {lastUpdated && (
                <span className="text-sm text-gray-500">
                  Last updated: {lastUpdated.toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
            </button>
            <button
              onClick={() => setShowThresholds(!showThresholds)}
              className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Settings className="w-4 h-4" />
              <span className="text-sm">Alert Thresholds</span>
            </button>
          </div>
        </div>
      </motion.div>

      {/* System Health Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Clock className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Uptime</p>
                    <p className="text-xl font-bold text-gray-900">
                      {health ? formatUptime(health.uptimeSeconds) : 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className={`p-2 rounded-lg ${getStatusBg(health?.neon)}`}>
                  <Database className={`w-6 h-6 ${getStatusColor(health?.neon)}`} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Neon DB</p>
                  <p className={`text-xl font-bold capitalize ${getStatusColor(health?.neon)}`}>
                    {health?.neon || 'Unknown'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className={`p-2 rounded-lg ${getStatusBg(health?.firebase)}`}>
                  <Cloud className={`w-6 h-6 ${getStatusColor(health?.firebase)}`} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Firebase</p>
                  <p className={`text-xl font-bold capitalize ${getStatusColor(health?.firebase)}`}>
                    {health?.firebase || 'Unknown'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className={`p-2 rounded-lg ${health?.recentErrors > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
                  <AlertCircle className={`w-6 h-6 ${health?.recentErrors > 0 ? 'text-red-600' : 'text-green-600'}`} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Errors (24h)</p>
                  <p className={`text-xl font-bold ${health?.recentErrors > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {health?.recentErrors || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Alert Thresholds Panel */}
      {showThresholds && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="mb-6"
        >
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Alert Thresholds</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">CPU Warning (%)</label>
                  <input
                    type="number"
                    value={thresholds.cpuWarning}
                    onChange={(e) => setThresholds(prev => ({ ...prev, cpuWarning: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">CPU Critical (%)</label>
                  <input
                    type="number"
                    value={thresholds.cpuCritical}
                    onChange={(e) => setThresholds(prev => ({ ...prev, cpuCritical: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Memory Warning (%)</label>
                  <input
                    type="number"
                    value={thresholds.memoryWarning}
                    onChange={(e) => setThresholds(prev => ({ ...prev, memoryWarning: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Resource Monitoring Gauges */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
        <Card className="mb-8">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <Cpu className="w-5 h-5 mr-2" />
              Resource Monitoring
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="flex flex-col items-center justify-center text-center py-4">
                <div className="mb-4">
                  <GaugeChart
                    value={metrics?.cpuUsage || 0}
                    max={100}
                    label="CPU Usage (%)"
                    color="#3b82f6"
                    size={120}
                  />
                </div>
                <div className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${getThresholdStatus(metrics?.cpuUsage || 0, thresholds.cpuWarning, thresholds.cpuCritical).bg} ${getThresholdStatus(metrics?.cpuUsage || 0, thresholds.cpuWarning, thresholds.cpuCritical).color}`}>
                  {getThresholdStatus(metrics?.cpuUsage || 0, thresholds.cpuWarning, thresholds.cpuCritical).label}
                </div>
              </div>
              <div className="flex flex-col items-center justify-center text-center py-4">
                <div className="mb-4">
                  <GaugeChart
                    value={metrics?.memoryUsage || 0}
                    max={100}
                    label="Memory Usage (%)"
                    color="#8b5cf6"
                    size={120}
                  />
                </div>
                <div className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${getThresholdStatus(metrics?.memoryUsage || 0, thresholds.memoryWarning, thresholds.memoryCritical).bg} ${getThresholdStatus(metrics?.memoryUsage || 0, thresholds.memoryWarning, thresholds.memoryCritical).color}`}>
                  {getThresholdStatus(metrics?.memoryUsage || 0, thresholds.memoryWarning, thresholds.memoryCritical).label}
                </div>
              </div>
              <div className="flex flex-col items-center justify-center text-center py-4">
                <div className="mb-4">
                  <GaugeChart
                    value={metrics?.errorRate || 0}
                    max={10}
                    label="Error Rate (%)"
                    color="#10b981"
                    size={120}
                  />
                </div>
                <div className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${getThresholdStatus(metrics?.errorRate || 0, thresholds.errorRateWarning, thresholds.errorRateCritical).bg} ${getThresholdStatus(metrics?.errorRate || 0, thresholds.errorRateWarning, thresholds.errorRateCritical).color}`}>
                  {getThresholdStatus(metrics?.errorRate || 0, thresholds.errorRateWarning, thresholds.errorRateCritical).label}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Performance Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Response Time Chart */}
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 }}>
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <TrendingUp className="w-5 h-5 mr-2" />
                Response Time (Last 24h)
              </h3>
              {metrics?.responseTimeData ? (
                <TrendChart
                  data={metrics.responseTimeData}
                  dataKeys={[{ dataKey: 'avgResponseTime', name: 'Avg Response (ms)' }]}
                  colors={['#3b82f6']}
                  type="line"
                  height={180}
                />
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  No data available
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Request Volume Chart */}
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.7 }}>
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Activity className="w-5 h-5 mr-2" />
                Request Volume (Last 24h)
              </h3>
              {metrics?.requestVolumeData ? (
                <TrendChart
                  data={metrics.requestVolumeData}
                  dataKeys={[{ dataKey: 'requests', name: 'Requests' }]}
                  colors={['#10b981']}
                  type="area"
                  height={180}
                />
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  No data available
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Activity Metrics */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}>
        <Card>
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Activity Metrics</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-gray-700">Requests/Min</p>
                  <Activity className="w-5 h-5 text-blue-600" />
                </div>
                <p className="text-2xl font-bold text-blue-600">
                  {metrics?.requestsPerMinute || 'N/A'}
                </p>
              </div>

              <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-gray-700">Audit Logs (24h)</p>
                  <HardDrive className="w-5 h-5 text-purple-600" />
                </div>
                <p className="text-2xl font-bold text-purple-600">
                  {metrics?.totalAuditLogs24h || 0}
                </p>
              </div>

              <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-gray-700">Last PDF</p>
                  <Clock className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-sm font-bold text-green-600">
                  {metrics?.lastPdfGeneratedAt 
                    ? new Date(metrics.lastPdfGeneratedAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    : 'Never'
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* System Info */}
      <motion.div 
        className="mt-6 text-center text-sm text-gray-500"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9 }}
      >
        <p>Last updated: {health ? new Date(health.timestamp).toLocaleString() : 'Unknown'}</p>
      </motion.div>
    </div>
  );
};

export default PerformancePageEnhanced;
